import type { FormKitOptionsItem } from '@formkit/inputs'
import type { CheckoutState, customerFormDataLocal } from '~/types/local-types'
import type { CountryOrderBuddy, DispatchTypeOrderBuddy, QuerycheckDeliveryAreaOrderBuddyArgs } from '~/types/orderbuddy-types'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import { apiCall } from '~/composables/apiCall'
import { countriesQuery } from '~/graphql/queries/countries.query.gql'
import { orderingDatesQuery } from '~/graphql/queries/orderingDates.query.gql'
import { orderingTimesQuery } from '~/graphql/queries/orderingTimes.query.gql'
import { account } from '~/store/account'
import { cart } from '~/store/cart'
import { webshopSettings } from '~/store/webshopSettings'
import { CustomerTypeOrderBuddy, PaymentMethodOrderBuddy, PayOnInvoiceStatusOrderBuddy } from '~/types/orderbuddy-types'
import { isCheckoutPath } from '~/utils/path-utils'
const defaultCustomerInput: customerFormDataLocal = {
    firstName: '',
    lastName: '',
    email: '',
    phoneGroup: {
        phone: '',
        phoneCountry: 'NL'
    },
    type: CustomerTypeOrderBuddy.CONSUMER,
    addressGroup: {
        street: '',
        city: '',
        postalCode: '',
        streetNumber: '',
        country: 'NL'
    },
    invAddressSame: true,
    saveAddress: true,
    billingContact: {
        company: '',
        name: '',
        email: '',
        city: '',
        postalCode: '',
        street: '',
        streetNumber: '',
        country: 'NL',
        phoneGroup: {
            phone: '',
            phoneCountry: 'NL'
        }
    }
}

export const checkout = defineStore('checkout', {
    state: (): CheckoutState => ({
        input: {
            tip: 0,
            deliveryAtDate: undefined,
            tipData: {
                tipRadio: webshopSettings().global.tip?.defaultPercentage ?? 0,
                customTip: 0
            },
            payment: {
                method: undefined
            },
            customer: defaultCustomerInput,
            dispatchType: webshopSettings().getDefaultDispatchType,
            dispatchTypeProxy: webshopSettings().getDefaultDispatchType
        },
        autoCompleteAddress: [],
        copiedCheckoutUser: false,
        datesOrTimesLoading: false,
        dates: null,
        availableTimes: [],
        countries: []
    } as CheckoutState),
    persist: {
        storage: piniaPluginPersistedstate.localStorage(),
        omit: [ 'availableTimes' ]
    },
    getters: {
        getCountriesForDropdown (): Array<FormKitOptionsItem> {
            return this?.countries?.map((country: CountryOrderBuddy) => ({
                label: country.alpha2Code,
                value: country.alpha2Code
            }))
        },
        isDateValid () {
            return (date?: Date) => {
                date = date ? new Date(date) : new Date()
                if (this?.dates?.startDate && date < new Date(this.dates.startDate)) {
                    return false
                }
                if (this?.dates?.endDate && date > new Date(this.dates.endDate)) {
                    return false
                }
                return !checkout()?.dates?.disallowedDates?.includes(date.toISOString().substring(0, 10))
            }
        },
        hasBasicAddress (): boolean {
            return this.input.customer.lastName !== '' || this.input.customer.firstName !== '' || this.input.customer.addressGroup.streetNumber !== '' || this.input.customer.addressGroup.postalCode !== ''
        },
        basicAddress (state) {
            return {
                firstName: state.input.customer.firstName,
                lastName: state.input.customer.lastName,
                postalCode: state.input.customer.addressGroup.postalCode,
                streetNumber: state.input.customer.addressGroup.streetNumber
            }
        },
        basicAddressSameAsAccount () {
            return isEqual(this.basicAddress, account().basicAddress)
        }
    },
    actions: {
        resetDefaultDispatchType () {
            if (!webshopSettings().global.dispatchTypes.includes(this.input.dispatchType)) {
                this.input.dispatchType = webshopSettings().getDefaultDispatchType
            }
        },
        resetPaymentMethod () {
            // If there are payment methods
            if (webshopSettings()?.global?.paymentMethods && webshopSettings()?.global?.paymentMethods?.length) {
                // But there is a selected payment method
                if (this.input.payment.method !== undefined) {
                    // If the selected payment method is not in the list of available payment methods, set it to undefined
                    if (!webshopSettings()?.global?.paymentMethods?.includes(this.input.payment.method)) {
                        if (this.input.payment.method === PaymentMethodOrderBuddy.CASH) {
                            delete this.input.payment.cashAmount
                        }
                        this.input.payment.method = undefined
                    } else {
                        // If the selected payment method is ACCOUNT, but the user is not allowed to use it (yet, or any more), then set it to undefined
                        if (this.input.payment.method === PaymentMethodOrderBuddy.ACCOUNT && account().userData.customer.payOnInvoiceStatus === PayOnInvoiceStatusOrderBuddy.INITIAL) {
                            this.input.payment.method = undefined
                        }
                    }
                } else {
                    // If there is only one payment method available, set it as the selected payment method
                    if (webshopSettings()?.global?.paymentMethods?.length === 1) {
                        this.input.payment.method = webshopSettings()?.global.paymentMethods![0]
                    }
                }
            } else {
                this.input.payment.method = undefined
            }
        },
        resetCustomerInput () {
            this.input.tip = 0
            this.input.deliveryAtDate = undefined
            this.input.tipData.tipRadio = webshopSettings().global.tip?.defaultPercentage ?? 0
            this.input.tipData.customTip = 0
            this.input.payment.cashAmount = null
            this.input.dispatchType = webshopSettings().getDefaultDispatchType
            this.dates = null
            this.availableTimes = []
            this.countries = []
            if (!this.input.customer.saveAddress) {
                this.input.customer = defaultCustomerInput
            }
        },
        async setDispatchTypeProxy (name: DispatchTypeOrderBuddy) {
            this.input.dispatchTypeProxy = name
        },
        async setDispatchType (name: DispatchTypeOrderBuddy, postActions: boolean = true) {
            this.input.dispatchType = name
            await checkout().setDispatchTypeProxy(name)
            if (import.meta.client) {
                cart().setCalculatorInputDatabase()
            }
            if (postActions) {
                const route = useRoute()
                if (isCheckoutPath(route.path)) {
                    await this.getDatesAndTimes()
                }
                await cart().updateLocalCartPrices()
                await cart().updateValidatedCartPrices()
            }
        },
        resetCashPayment () {
            this.input.payment.cashAmount = null
        },
        setCustomerAdressGroup (postalCode: string, streetNumber: string) {
            this.input.customer.addressGroup.postalCode = postalCode
            this.input.customer.addressGroup.streetNumber = streetNumber
        },
        async getAvailableTimes () {
            if (this?.input?.deliveryAtDate) {
                // Dates should be formatted in YYYY-MM-DD
                const data = await apiCall(orderingTimesQuery, 'orderingTimes', {
                    dispatchType: this.input.dispatchType,
                    date: new Date(this.input.deliveryAtDate).toISOString().substring(0, 10)
                }, false)
                if (data?.times) {
                    this.availableTimes = data.times
                    this.setTargetTime()
                }
            }
        },
        setTargetTime () {
            if (this.availableTimes?.length === 0 || !this.availableTimes?.includes(this.input.deliveryAtTime)) {
                // If there are no times or if the selected time is not available, reset the time input value
                this.input.deliveryAtTime = undefined
            }
        },
        async getDatesAndTimes () {
            this.datesOrTimesLoading = true
            await this.getDates()
            await this.getAvailableTimes()
            this.datesOrTimesLoading = false
        },
        async getDates () {
            const data = await apiCall(orderingDatesQuery, 'orderingDates', {
                dispatchType: this.input.dispatchType
            }, false)
            if (data) {
                this.dates = data
                this.setTargetDate()
            }
        },
        setTargetDate () {
            const hasDeliveryAtDate = this?.input?.deliveryAtDate !== undefined
            if (!hasDeliveryAtDate && this.isDateValid()) {
                // If the today is a valid date, then set today as the deliveryAtDate
                this.input.deliveryAtDate = new Date()
            } else if (hasDeliveryAtDate && !this.isDateValid(this.input.deliveryAtDate)) {
                // If there is deliveryAtDate, but it is invalid
                if (new Date(this.input.deliveryAtDate) < new Date()) {
                    // If the current deliveryAtDate is in the past
                    if (this.isDateValid()) {
                        // If today is a valid date, then set today as the deliveryAtDate
                        this.input.deliveryAtDate = new Date()
                    } else {
                        // If today is not a valid date, then reset deliveryAtDate to force the user to make a new choice
                        this.input.deliveryAtDate = undefined
                    }
                } else {
                    // If the deliveryAtDate is invalid, but not in the past, then reset deliveryAtDate to force the user to make a new choice
                    this.input.deliveryAtDate = undefined
                }
            }
        },
        async fetchCountries () {
            if (this.countries.length === 0) {
                this.countries = await apiCall(countriesQuery, 'countries', {}, false)
            }
        },
        setUserDeliveryAddress (data: QuerycheckDeliveryAreaOrderBuddyArgs) {
            this.input.customer.addressGroup.streetNumber = data?.streetNumber ?? ''
            this.input.customer.addressGroup.postalCode = data?.postalCode ?? ''
        },
        copyAddressFromAccount () {
            this.input.customer = cloneDeep(account().userData.customer)
            this.copiedCheckoutUser = true
        },
        autoFillCheckoutUser () {
            if (account().hasBasicAddress && !this.hasBasicAddress) {
                this.copyAddressFromAccount()
            }
        },
        resetCopiedCheckoutUser () {
            this.copiedCheckoutUser = false
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(checkout, import.meta.hot))
}
